// ======================================================================
// FILE: src/modules/wms/escalation/components/EscalationLevelsTab.js
// MODULE: Work Management System (WMS)
// FEATURE: Escalation Levels Management
//
// PURPOSE:
//   Manage Escalation Levels
//   Manage Escalation Recipients
//   Team Based Escalation Configuration
//
// DEPENDENCIES:
//   wmsEscalationService
//   wmsTeamService
//
// ARCHITECTURE:
//   ✓ TaskFlow ERP Standard
//   ✓ AdminLTE
//   ✓ Multi Tenant
//   ✓ Multi Location
//   ✓ Audit Enabled
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import wmsEscalationService
from "../../services/wmsEscalationService";

import wmsTeamService
from "../../services/wmsTeamService";

const TRIGGER_TYPES = [

  {
    value: "WARNING",
    label: "Warning",
  },

  {
    value: "BREACH",
    label: "Breach",
  },

  {
    value: "AFTER_BREACH",
    label: "After Breach",
  },
];

const RECIPIENT_TYPES = [

  {
    value: "TEAM",
    label: "Team",
  },

  {
    value: "LOCATION_ADMIN",
    label: "Location Admin",
  },
];

export default function EscalationLevelsTab({

  policyId,

}) {

  const [loading, setLoading] =
    useState(false);

  const [levels, setLevels] =
    useState([]);

  const [teams, setTeams] =
    useState([]);

  const [showLevelModal,
    setShowLevelModal] =
      useState(false);

  const [showRecipientModal,
    setShowRecipientModal] =
      useState(false);

  const [selectedLevel,
    setSelectedLevel] =
      useState(null);

  const [recipientLevel,
    setRecipientLevel] =
      useState(null);

  const [recipientRows,
    setRecipientRows] =
      useState([]);

  const [teamMembers,
    setTeamMembers] =
      useState([]);

  const [levelForm,
    setLevelForm] =
      useState({

        id: null,

        level_no: "",

        escalation_trigger_type:
          "WARNING",

        trigger_after_minutes:
          0,

        send_email: true,

        send_notification: true,

        remarks: "",
      });

  const [recipientForm,
    setRecipientForm] =
      useState({

        recipient_type:
          "TEAM",

        recipient_id: "",

        is_mandatory_acknowledgement:
          false,

        remarks: "",
      });

  // ==========================================================
  // LOAD
  // ==========================================================

  useEffect(() => {

    if (!policyId) {

      return;
    }

    loadLevels();

    loadTeams();

  }, [policyId]);

  async function loadLevels() {

    try {

      setLoading(true);

      const data =
        await wmsEscalationService
          .getLevels(policyId);

      setLevels(
        data || []
      );

    } catch (err) {

      alert(
        err?.response?.data?.message
        ||
        "Failed to load levels"
      );

    } finally {

      setLoading(false);
    }
  }

  async function loadTeams() {

    try {

      const result =
        await wmsTeamService
          .listTeams({

            page: 1,
            per_page: 1000,
          });

      setTeams(
        result.items || []
      );

    } catch (err) {

      console.error(err);
    }
  }

  async function loadRecipients(
    level
  ) {

    try {

      const rows =
        await wmsEscalationService
          .getRecipients(
            level.id
          );

      setRecipientRows(
        rows || []
      );

      setRecipientLevel(
        level
      );

      setShowRecipientModal(
        true
      );

    } catch (err) {

      alert(
        err?.response?.data?.message
        ||
        "Failed to load recipients"
      );
    }
  }

  async function loadTeamMembers(
    teamId
  ) {

    if (!teamId) {

      setTeamMembers([]);

      return;
    }

    try {

      const members =
        await wmsTeamService
          .getMembers(teamId);

      setTeamMembers(
        members || []
      );

    } catch (err) {

      console.error(err);

      setTeamMembers([]);
    }
  }

  // ==========================================================
  // LEVEL MODAL
  // ==========================================================

  function openCreateLevel() {

    setSelectedLevel(
      null
    );

    setLevelForm({

      id: null,

      level_no: "",

      escalation_trigger_type:
        "WARNING",

      trigger_after_minutes:
        0,

      send_email: true,

      send_notification: true,

      remarks: "",
    });

    setShowLevelModal(
      true
    );
  }

  function openEditLevel(
    level
  ) {

    setSelectedLevel(
      level
    );

    setLevelForm({

      id: level.id,

      level_no:
        level.level_no,

      escalation_trigger_type:
        level.escalation_trigger_type,

      trigger_after_minutes:
        level.trigger_after_minutes,

      send_email:
        level.send_email,

      send_notification:
        level.send_notification,

      remarks:
        level.remarks || "",
    });

    setShowLevelModal(
      true
    );
  }
    // ==========================================================
  // LEVEL CRUD
  // ==========================================================

  async function saveLevel() {

    try {

      if (!levelForm.level_no) {

        alert(
          "Level No is required"
        );

        return;
      }

      if (
        !levelForm
          .escalation_trigger_type
      ) {

        alert(
          "Trigger Type is required"
        );

        return;
      }

      if (
        levelForm
          .trigger_after_minutes === ""
      ) {

        alert(
          "Trigger After Minutes is required"
        );

        return;
      }

      const payload = {

        level_no:
          Number(
            levelForm.level_no
          ),

        escalation_trigger_type:
          levelForm
            .escalation_trigger_type,

        trigger_after_minutes:
          Number(
            levelForm
              .trigger_after_minutes
          ),

        send_email:
          levelForm.send_email,

        send_notification:
          levelForm
            .send_notification,

        remarks:
          levelForm.remarks,
      };

      if (selectedLevel) {

        await wmsEscalationService
          .updateLevel(
            policyId,
            selectedLevel.id,
            payload
          );

      } else {

        await wmsEscalationService
          .createLevel(
            policyId,
            payload
          );
      }

      setShowLevelModal(
        false
      );

      loadLevels();

    } catch (err) {

      alert(
        err?.response?.data?.message
        ||
        "Failed to save level"
      );
    }
  }

  async function deleteLevel(
    level
  ) {

    const ok =
      window.confirm(
        `Delete Level ${level.level_no}?`
      );

    if (!ok) {

      return;
    }

    try {

      await wmsEscalationService
        .deleteLevel(
          policyId,
          level.id
        );

      loadLevels();

    } catch (err) {

      alert(
        err?.response?.data?.message
        ||
        "Delete failed"
      );
    }
  }

  // ==========================================================
  // RECIPIENT CRUD
  // ==========================================================

  function openCreateRecipient() {

    setRecipientForm({

      recipient_type:
        "TEAM",

      recipient_id: "",

      is_mandatory_acknowledgement:
        false,

      remarks: "",
    });

    setTeamMembers([]);
  }

  function handleRecipientTypeChange(
    value
  ) {

    setRecipientForm({

      ...recipientForm,

      recipient_type: value,

      recipient_id: "",
    });

    setTeamMembers([]);
  }

  async function handleTeamChange(
    teamId
  ) {

    setRecipientForm({

      ...recipientForm,

      recipient_id: teamId,
    });

    await loadTeamMembers(
      teamId
    );
  }
    // ==========================================================
  // SAVE RECIPIENT
  // ==========================================================

  async function saveRecipient() {

    try {

      if (
        !recipientForm.recipient_type
      ) {

        alert(
          "Recipient Type is required"
        );

        return;
      }

      if (
        recipientForm.recipient_type ===
        "TEAM"
      ) {

        if (
          !recipientForm.recipient_id
        ) {

          alert(
            "Recipient Team is required"
          );

          return;
        }
      }

      const payload = {

        recipient_type:
          recipientForm.recipient_type,

        recipient_id:
          recipientForm.recipient_type ===
          "LOCATION_ADMIN"
            ? null
            : Number(
                recipientForm.recipient_id
              ),

        is_mandatory_acknowledgement:
          recipientForm.is_mandatory_acknowledgement,

        remarks:
          recipientForm.remarks,
      };

      await wmsEscalationService
        .createRecipient(
          recipientLevel.id,
          payload
        );

      const rows =
        await wmsEscalationService
          .getRecipients(
            recipientLevel.id
          );

      setRecipientRows(
        rows || []
      );

      setRecipientForm({

        recipient_type:
          "TEAM",

        recipient_id: "",

        is_mandatory_acknowledgement:
          false,

        remarks: "",
      });

      setTeamMembers([]);

    } catch (err) {

      alert(
        err?.response?.data?.message
        ||
        "Failed to save recipient"
      );
    }
  }

  // ==========================================================
  // DELETE RECIPIENT
  // ==========================================================

  async function deleteRecipient(
    recipient
  ) {

    const ok =
      window.confirm(
        "Remove recipient?"
      );

    if (!ok) {

      return;
    }

    try {

      await wmsEscalationService
        .deleteRecipient(
          recipientLevel.id,
          recipient.id
        );

      const rows =
        await wmsEscalationService
          .getRecipients(
            recipientLevel.id
          );

      setRecipientRows(
        rows || []
      );

    } catch (err) {

      alert(
        err?.response?.data?.message
        ||
        "Failed to delete recipient"
      );
    }
  }

  // ==========================================================
  // GRID UI
  // ==========================================================

  return (

    <>

      <div className="card">

        <div className="card-header d-flex justify-content-between align-items-center">

          <div>

            <strong>
              Escalation Levels
            </strong>

          </div>

          <button
            className="btn btn-primary btn-sm"
            onClick={
              openCreateLevel
            }
          >
            + Add Level
          </button>

        </div>

        <div className="card-body p-0">

          <table
            className="table table-hover table-striped align-middle mb-0"
          >

            <thead className="table-light">

              <tr>

                <th>
                  Level
                </th>

                <th>
                  Trigger Type
                </th>

                <th>
                  Minutes
                </th>

                <th>
                  Email
                </th>

                <th>
                  Notification
                </th>

                <th>
                  Recipients
                </th>

                <th width="240">
                  Actions
                </th>

              </tr>

            </thead>

            <tbody>

              {loading && (

                <tr>

                  <td
                    colSpan="7"
                    className="text-center"
                  >
                    Loading...
                  </td>

                </tr>

              )}

              {!loading &&
                levels.length === 0 && (

                <tr>

                  <td
                    colSpan="7"
                    className="text-center"
                  >
                    No Levels Configured
                  </td>

                </tr>

              )}

              {!loading &&
                levels.map(
                  (level) => (

                  <tr
                    key={level.id}
                  >

                    <td>
                      {
                        level.level_no
                      }
                    </td>

                    <td>
                      {
                        level.escalation_trigger_type
                      }
                    </td>

                    <td>
                      {
                        level.trigger_after_minutes
                      }
                    </td>

                    <td>

                      <span
                        className={
                          level.send_email
                            ? "badge bg-success"
                            : "badge bg-secondary"
                        }
                      >
                        {
                          level.send_email
                            ? "Yes"
                            : "No"
                        }
                      </span>

                    </td>

                    <td>

                      <span
                        className={
                          level.send_notification
                            ? "badge bg-success"
                            : "badge bg-secondary"
                        }
                      >
                        {
                          level.send_notification
                            ? "Yes"
                            : "No"
                        }
                      </span>

                    </td>

                    <td>
                      {
                        level.recipient_count
                        || 0
                      }
                    </td>

                    <td>

                      <button
                        className="btn btn-sm btn-info me-1"
                        onClick={() =>
                          loadRecipients(
                            level
                          )
                        }
                      >
                        Recipients
                      </button>

                      <button
                        className="btn btn-sm btn-primary me-1"
                        onClick={() =>
                          openEditLevel(
                            level
                          )
                        }
                      >
                        Edit
                      </button>

                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() =>
                          deleteLevel(
                            level
                          )
                        }
                      >
                        Delete
                      </button>

                    </td>

                  </tr>

                ))}

            </tbody>

          </table>

        </div>

      </div>
            {/* ===================================================== */}
      {/* LEVEL MODAL */}
      {/* ===================================================== */}

      {showLevelModal && (

        <div
          className="modal fade show"
          style={{
            display: "block",
            background:
              "rgba(0,0,0,0.5)",
          }}
        >

          <div className="modal-dialog modal-lg">

            <div className="modal-content">

              <div className="modal-header">

                <h5 className="modal-title">

                  {selectedLevel
                    ? "Edit Escalation Level"
                    : "Add Escalation Level"}

                </h5>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() =>
                    setShowLevelModal(
                      false
                    )
                  }
                />

              </div>

              <div className="modal-body">

                <div className="small text-muted mb-3">

                  Fields marked with

                  <span className="text-danger ms-1">
                    *
                  </span>

                  {" "}are mandatory.

                </div>

                <div className="row">

                  <div className="col-md-3 mb-3">

                    <label>

                      Level No

                      <span className="text-danger ms-1">
                        *
                      </span>

                    </label>

                    <input
                      type="number"
                      className="form-control"
                      value={
                        levelForm.level_no
                      }
                      onChange={(e) =>
                        setLevelForm({

                          ...levelForm,

                          level_no:
                            e.target.value,
                        })
                      }
                    />

                  </div>

                  <div className="col-md-4 mb-3">

                    <label>

                      Trigger Type

                      <span className="text-danger ms-1">
                        *
                      </span>

                    </label>

                    <select
                      className="form-control"
                      value={
                        levelForm.escalation_trigger_type
                      }
                      onChange={(e) =>
                        setLevelForm({

                          ...levelForm,

                          escalation_trigger_type:
                            e.target.value,
                        })
                      }
                    >

                      {TRIGGER_TYPES.map(
                        (item) => (

                        <option
                          key={item.value}
                          value={item.value}
                        >
                          {item.label}
                        </option>

                        )
                      )}

                    </select>

                  </div>

                  <div className="col-md-5 mb-3">

                    <label>

                      Trigger After Minutes

                      <span className="text-danger ms-1">
                        *
                      </span>

                    </label>

                    <input
                      type="number"
                      className="form-control"
                      value={
                        levelForm.trigger_after_minutes
                      }
                      onChange={(e) =>
                        setLevelForm({

                          ...levelForm,

                          trigger_after_minutes:
                            e.target.value,
                        })
                      }
                    />

                  </div>

                  <div className="col-md-3">

                    <div className="form-check">

                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={
                          levelForm.send_email
                        }
                        onChange={(e) =>
                          setLevelForm({

                            ...levelForm,

                            send_email:
                              e.target.checked,
                          })
                        }
                      />

                      <label
                        className="form-check-label"
                      >
                        Send Email
                      </label>

                    </div>

                  </div>

                  <div className="col-md-4">

                    <div className="form-check">

                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={
                          levelForm.send_notification
                        }
                        onChange={(e) =>
                          setLevelForm({

                            ...levelForm,

                            send_notification:
                              e.target.checked,
                          })
                        }
                      />

                      <label
                        className="form-check-label"
                      >
                        Send Notification
                      </label>

                    </div>

                  </div>

                  <div className="col-md-12 mt-3">

                    <label>
                      Remarks
                    </label>

                    <textarea
                      rows="3"
                      className="form-control"
                      value={
                        levelForm.remarks
                      }
                      onChange={(e) =>
                        setLevelForm({

                          ...levelForm,

                          remarks:
                            e.target.value,
                        })
                      }
                    />

                  </div>

                </div>

              </div>

              <div className="modal-footer">

                <button
                  className="btn btn-primary"
                  onClick={saveLevel}
                >
                  Save
                </button>

                <button
                  className="btn btn-secondary"
                  onClick={() =>
                    setShowLevelModal(
                      false
                    )
                  }
                >
                  Cancel
                </button>

              </div>

            </div>

          </div>

        </div>

      )}

      {/* ===================================================== */}
      {/* RECIPIENT MODAL */}
      {/* ===================================================== */}

      {showRecipientModal && (

        <div
          className="modal fade show"
          style={{
            display: "block",
            background:
              "rgba(0,0,0,0.5)",
          }}
        >

          <div className="modal-dialog modal-xl">

            <div className="modal-content">

              <div className="modal-header">

                <h5 className="modal-title">

                  Recipients

                  {" - "}

                  Level

                  {" "}

                  {
                    recipientLevel?.level_no
                  }

                </h5>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() =>
                    setShowRecipientModal(
                      false
                    )
                  }
                />

              </div>

              <div className="modal-body">

                <div className="mb-3 text-end">

                  <button
                    className="btn btn-primary btn-sm"
                    onClick={
                      openCreateRecipient
                    }
                  >
                    + Add Recipient
                  </button>

                </div>

                {/* Recipient form section will be displayed here */}
                <div className="card mb-3">

                    <div className="card-header">

                        <strong>
                        Add Recipient
                        </strong>

                    </div>

                    <div className="card-body">

                        <div className="row">

                        <div className="col-md-4">

                            <label>

                            Recipient Type

                            <span className="text-danger ms-1">
                                *
                            </span>

                            </label>

                            <select
                            className="form-control"
                            value={
                                recipientForm.recipient_type
                            }
                            onChange={(e) =>
                                handleRecipientTypeChange(
                                e.target.value
                                )
                            }
                            >

                            {RECIPIENT_TYPES.map(
                                (item) => (

                                <option
                                key={item.value}
                                value={item.value}
                                >
                                {item.label}
                                </option>

                                )
                            )}

                            </select>

                        </div>

                        {recipientForm.recipient_type ===
                            "TEAM" && (

                            <div className="col-md-5">

                            <label>

                                Recipient Team

                                <span className="text-danger ms-1">
                                *
                                </span>

                            </label>

                            <select
                                className="form-control"
                                value={
                                recipientForm.recipient_id
                                }
                                onChange={(e) =>
                                handleTeamChange(
                                    e.target.value
                                )
                                }
                            >

                                <option value="">
                                Select Team
                                </option>

                                {teams.map(
                                (team) => (

                                <option
                                    key={team.id}
                                    value={team.id}
                                >
                                    {
                                    team.team_name
                                    ||
                                    team.team_code
                                    }
                                </option>

                                )
                                )}

                            </select>

                            </div>

                        )}

                        <div className="col-md-3">

                            <label>
                            &nbsp;
                            </label>

                            <div>

                            <button
                                className="btn btn-primary w-100"
                                onClick={saveRecipient}
                            >
                                Add Recipient
                            </button>

                            </div>

                        </div>

                        </div>

                        <div className="row mt-3">

                        <div className="col-md-4">

                            <div className="form-check">

                            <input
                                type="checkbox"
                                className="form-check-input"
                                checked={
                                recipientForm
                                    .is_mandatory_acknowledgement
                                }
                                onChange={(e) =>
                                setRecipientForm({

                                    ...recipientForm,

                                    is_mandatory_acknowledgement:
                                    e.target.checked,
                                })
                                }
                            />

                            <label
                                className="form-check-label"
                            >
                                Mandatory
                                Acknowledgement
                            </label>

                            </div>

                        </div>

                        </div>

                        <div className="row mt-3">

                        <div className="col-md-12">

                            <label>
                            Remarks
                            </label>

                            <textarea
                            rows="2"
                            className="form-control"
                            value={
                                recipientForm.remarks
                            }
                            onChange={(e) =>
                                setRecipientForm({

                                ...recipientForm,

                                remarks:
                                    e.target.value,
                                })
                            }
                            />

                        </div>

                        </div>

                    </div>

                    </div>

                {/* TEAM MEMBER PREVIEW */}

                {recipientForm.recipient_type ===
                  "TEAM" &&
                  teamMembers.length > 0 && (

                  <div className="card mb-3">

                    <div className="card-header">

                      Team Members

                    </div>

                    <div className="card-body">

                      <ul className="mb-0">

                        {teamMembers.map(
                          (member) => (

                          <li
                            key={member.id}
                          >

                            {
                              member.employee_name
                              ||
                              member.member_name
                              ||
                              member.user_name
                              ||
                              member.id
                            }

                          </li>

                          )
                        )}

                      </ul>

                    </div>

                  </div>

                )}

                <table
                  className="table table-striped table-hover"
                >

                  <thead>

                    <tr>

                      <th>
                        Recipient Type
                      </th>

                      <th>
                        Recipient
                      </th>

                      <th>
                        Mandatory Ack
                      </th>

                      <th width="120">
                        Actions
                      </th>

                    </tr>

                  </thead>

                  <tbody>

                    {recipientRows.map(
                      (row) => (

                      <tr
                        key={row.id}
                      >

                        <td>
                          {
                            row.recipient_type
                          }
                        </td>

                        <td>
                          {
                            row.recipient_name
                            ||
                            row.recipient_id
                          }
                        </td>

                        <td>

                          <span
                            className={
                              row.is_mandatory_acknowledgement
                                ? "badge bg-success"
                                : "badge bg-secondary"
                            }
                          >
                            {
                              row.is_mandatory_acknowledgement
                                ? "Yes"
                                : "No"
                            }
                          </span>

                        </td>

                        <td>

                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() =>
                                deleteRecipient(
                                row
                                )
                            }
                            >
                            Delete
                            </button>

                        </td>

                      </tr>

                    ))}

                  </tbody>

                </table>

              </div>

              <div className="modal-footer">

                <button
                  className="btn btn-secondary"
                  onClick={() =>
                    setShowRecipientModal(
                      false
                    )
                  }
                >
                  Close
                </button>

              </div>

            </div>

          </div>

        </div>

      )}

    </>

  );
}